import { useEffect, useRef } from "react";
import "./Iridescence.css";

const VERTEX = `#version 300 es
in vec2 aPosition;
out vec2 vUv;
void main() {
  vUv = aPosition * 0.5 + 0.5;
  gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

const FRAGMENT = `#version 300 es
precision highp float;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uColor;
uniform float uScale;
uniform float uRotation;
uniform int uDetail;
uniform float uWarp;
uniform float uHue;
uniform float uSaturation;
uniform float uBrightness;
uniform float uContrast;
in vec2 vUv;
out vec4 outColor;

vec3 hueRotate(vec3 c, float angle) {
  const vec3 k = vec3(0.57735027);
  float ca = cos(angle);
  return c * ca + cross(k, c) * sin(angle) + k * dot(k, c) * (1.0 - ca);
}

void main() {
  float mr = min(uResolution.x, uResolution.y);
  vec2 uv = (vUv * 2.0 - 1.0) * uResolution / mr;
  float c = cos(uRotation);
  float s = sin(uRotation);
  uv = mat2(c, -s, s, c) * uv / uScale;

  float d = -uTime * 0.5;
  float a = 0.0;
  for (int i = 0; i < 24; i++) {
    if (i >= uDetail) break;
    float fi = float(i);
    a += cos(fi - d - a * uv.x * uWarp);
    d += sin(uv.y * fi + a);
  }
  d += uTime * 0.5;
  vec3 col = vec3(cos(uv * vec2(d, a)) * 0.6 + 0.4, cos(a + d) * 0.5 + 0.5);
  col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5) * uColor;
  col = hueRotate(col, uHue);
  float luma = dot(col, vec3(0.2126, 0.7152, 0.0722));
  col = mix(vec3(luma), col, uSaturation);
  col = (col - 0.5) * uContrast + 0.5;
  col *= uBrightness;
  outColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}`;

// The login background uses the supplied shader without pointer, grain, or fade effects.
export default function Iridescence({
  color = "#52b87f",
  hueShift = -1,
  saturation = 1,
  brightness = 1.7,
  contrast = 0.6,
  speed = 0.5,
  scale = 1,
  detail = 7,
  warp = 1,
  rotation = 0,
}) {
  const rootRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    const gl = canvas.getContext("webgl2", { antialias: false });
    if (!gl) {
      console.warn("Iridescence: WebGL 2 is unavailable; using the static login gradient.");
      return undefined;
    }

    const shaders = [];
    let program;
    let buffer;
    const release = () => {
      if (buffer) gl.deleteBuffer(buffer);
      if (program) gl.deleteProgram(program);
      shaders.forEach(shader => gl.deleteShader(shader));
    };
    const compile = (type, source) => {
      const shader = gl.createShader(type);
      if (!shader) throw new Error("Could not create shader.");
      shaders.push(shader);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) || "Shader compilation failed.");
      }
      return shader;
    };

    let uniforms;
    try {
      const vertex = compile(gl.VERTEX_SHADER, VERTEX);
      const fragment = compile(gl.FRAGMENT_SHADER, FRAGMENT);
      program = gl.createProgram();
      if (!program) throw new Error("Could not create shader program.");
      gl.attachShader(program, vertex);
      gl.attachShader(program, fragment);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) || "Shader linking failed.");
      }
      gl.useProgram(program);
      buffer = gl.createBuffer();
      if (!buffer) throw new Error("Could not create vertex buffer.");
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const position = gl.getAttribLocation(program, "aPosition");
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      uniforms = Object.fromEntries([
        "uResolution", "uTime", "uColor", "uScale", "uRotation",
        "uDetail", "uWarp", "uHue", "uSaturation", "uBrightness", "uContrast",
      ].map(name => [name, gl.getUniformLocation(program, name)]));
      if (!/^#[\da-f]{6}$/i.test(color)) {
        throw new Error("The background tint must be a six-digit hex color.");
      }
    } catch (error) {
      console.error("Iridescence: initialization failed; using the static login gradient.", error);
      release();
      return undefined;
    }

    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let visible = root.clientWidth > 0 && root.clientHeight > 0;
    let frameId = 0;
    let last = 0;
    let time = 0;
    let contextLost = false;
    const draw = () => {
      if (contextLost || !root.clientWidth || !root.clientHeight) return;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uniforms.uResolution, root.clientWidth, root.clientHeight);
      gl.uniform1f(uniforms.uTime, time);
      gl.uniform3f(uniforms.uColor,
        parseInt(color.slice(1, 3), 16) / 255,
        parseInt(color.slice(3, 5), 16) / 255,
        parseInt(color.slice(5, 7), 16) / 255);
      gl.uniform1f(uniforms.uScale, Math.max(0.05, scale));
      gl.uniform1f(uniforms.uRotation, rotation * Math.PI / 180);
      gl.uniform1i(uniforms.uDetail, Math.min(24, Math.max(1, Math.round(detail))));
      gl.uniform1f(uniforms.uWarp, warp);
      gl.uniform1f(uniforms.uHue, hueShift * Math.PI / 180);
      gl.uniform1f(uniforms.uSaturation, Math.max(0, saturation));
      gl.uniform1f(uniforms.uBrightness, Math.max(0, brightness));
      gl.uniform1f(uniforms.uContrast, Math.max(0, contrast));
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };
    const stop = () => {
      cancelAnimationFrame(frameId);
      frameId = 0;
      last = 0;
    };
    const frame = now => {
      frameId = 0;
      if (!visible || document.hidden || contextLost) return;
      if (last) time += Math.min(0.05, (now - last) / 1000) * speed;
      last = now;
      draw();
      if (!motion.matches && speed !== 0) frameId = requestAnimationFrame(frame);
    };
    const wake = () => {
      stop();
      if (!visible || document.hidden || contextLost) return;
      draw();
      if (!motion.matches && speed !== 0) frameId = requestAnimationFrame(frame);
    };
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(root.clientWidth * dpr));
      canvas.height = Math.max(1, Math.round(root.clientHeight * dpr));
      visible = root.clientWidth > 0 && root.clientHeight > 0;
      wake();
    };
    const onContextLost = event => {
      event.preventDefault();
      contextLost = true;
      canvas.style.visibility = "hidden";
      stop();
      console.warn("Iridescence: WebGL context lost; using the static login gradient.");
    };
    const resizeObserver = new ResizeObserver(resize);
    const intersectionObserver = new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting);
      wake();
    });
    resizeObserver.observe(root);
    intersectionObserver.observe(root);
    motion.addEventListener("change", wake);
    document.addEventListener("visibilitychange", wake);
    canvas.addEventListener("webglcontextlost", onContextLost);
    resize();

    return () => {
      stop();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      motion.removeEventListener("change", wake);
      document.removeEventListener("visibilitychange", wake);
      canvas.removeEventListener("webglcontextlost", onContextLost);
      release();
    };
  }, [color, hueShift, saturation, brightness, contrast, speed, scale, detail, warp, rotation]);

  return (
    <div ref={rootRef} className="iridescence-container" aria-hidden="true">
      <canvas ref={canvasRef} className="iridescence-canvas" />
    </div>
  );
}
